import { useId } from 'react'
import { getOverview } from '../../lib/api'
import type { DefenderReading } from '../../lib/api'
import { notLicensedOf } from './notLicensed'
import { agrees, useSession } from './session'
import { reasonClause } from './unreadReasons'
import { useApi } from './useApi'
import './live.css'
import './secure-score.css'

/** Microsoft's own exposure management pages. Theirs to explain, so we send people there. */
const DEFENDER_URL = 'https://security.microsoft.com/exposure-management'

/**
 * Microsoft Defender's exposure score, and the one line about the vulnerabilities that are not
 * findings -- the top of the Endpoint page (owner, 2026-09-25: "for endpoint we should have exposure
 * score from Microsoft Defender").
 *
 * MICROSOFT'S NUMBER, attributed in words beside it, as Secure Score is (SecureScoreGauge.tsx). It
 * used to be a finding raised above a threshold we chose; it is a reading now (0153). LOWER IS
 * BETTER, the opposite of Secure Score, so the page says so and prints Microsoft's own level --
 * Microsoft's documented bands, 0-29 low, 30-69 medium, 70-100 high -- rather than leaving a reader
 * to guess which way is good. The arc stays the neutral accent: colouring it would be us judging it.
 *
 * Only a measured score draws a gauge; "not read", "Microsoft has none" and "not read yet" are
 * sentences, never an empty arc.
 */
export function ExposureScoreGauge({
  reading,
  notLicensed = false,
  compact = false,
}: {
  reading: DefenderReading | null | undefined
  /**
   * The latest assessment did not assess Microsoft Defender for Endpoint because this tenant has no
   * licence for it (0158). Microsoft then has no score or vulnerability list to give, and "we
   * couldn't read it" would blame a permission for a product the customer never bought.
   */
  notLicensed?: boolean

  /** As the last tile of the Endpoint page's insights strip: the same figure and sentences, no arc. */
  compact?: boolean
}) {
  const headingId = useId()
  const exposure = reading?.exposure
  // Rounded ONCE, and the level taken from the number printed: 29.6 must not print "30" beside
  // "Low exposure".
  const measured =
    exposure?.status === 'measured' && typeof exposure.score === 'number' && Number.isFinite(exposure.score)
      ? Math.round(exposure.score)
      : null

  // Not licensed is said only where Microsoft gave us nothing at all. Anything it did measure is
  // shown as measured -- the flag and the reading can come from different assessments (the reading
  // is the latest that read Defender), and a count Microsoft reported is never hidden behind "there
  // is no vulnerability list" (review, 2026-09-26).
  const saysNotLicensed = notLicensed && measured === null && reading?.vulnerabilities.status !== 'measured'

  // Microsoft Defender listed NO onboarded devices on this read (0159), conclusively: the list
  // answered, complete, and empty. Microsoft then shows an exposure score of 0 -- not a measurement
  // of anything, and drawn as "Low exposure" it is the flattering zero this product refuses. A
  // reading from before 0159, a list not read, or one cut short is none of this: today's panel.
  const noDevices = noOnboardedDevices(reading)

  // The number the panel draws: Microsoft's, unless it is the 0 it gives for nothing to measure.
  const shown = noDevices && measured === 0 ? null : measured

  if (compact) {
    return (
      <div className="fi-tile fi-static fi-area-tile" role="group" aria-labelledby={headingId}>
        <span id={headingId} className="fi-label">Defender exposure score</span>
        {shown !== null ? (
          <>
            <span className="fi-value">{shown}<small>/100</small></span>
            <span className="fi-sub">
              {capitalise(level(shown))} exposure · Microsoft&apos;s own number, lower is better
              {noDevices ? `. ${NO_DEVICES_NOTE}` : ''}
            </span>
          </>
        ) : (
          <span className="fi-sub">
            {saysNotLicensed
              ? NOT_LICENSED_SENTENCE
              : noDevices && measured === 0
                ? NO_DEVICES_ZERO_SENTENCE
                : exposureSentence(reading, noDevices)}
          </span>
        )}
        {saysNotLicensed ? null : <span className="fi-sub">{vulnerabilitySentence(reading, noDevices)}</span>}
        {shown !== null || reading?.vulnerabilities.status === 'measured' ? (
          <a className="fi-link" href={DEFENDER_URL} target="_blank" rel="noopener noreferrer">Open in Microsoft Defender</a>
        ) : null}
      </div>
    )
  }

  return (
    <section className="dash-panel secure-score" aria-labelledby={headingId}>
      {shown !== null ? (
        <ExposureArc score={shown} label={`Microsoft Defender exposure score ${shown} out of 100, ${level(shown)} exposure`} />
      ) : null}

      <div className="secure-score-body">
        <h2 id={headingId} className="dash-panel-title">Microsoft Defender exposure score</h2>
        <p className="secure-score-attribution">
          Microsoft&apos;s own measure of how exposed your devices are to known vulnerabilities, as
          Microsoft reports it. Lower is better. Not a Defenssive score.
        </p>

        {shown !== null ? (
          <>
            <p className="secure-score-points">{capitalise(level(shown))} exposure</p>
            {exposure?.measuredAt ? (
              <p className="secure-score-note">Microsoft&apos;s score of {day(exposure.measuredAt)}</p>
            ) : null}
            {/* Shown as measured -- a number Microsoft reported is never hidden -- but not without
                saying it came from a Defender that listed no onboarded devices. */}
            {noDevices ? <p className="secure-score-note">{NO_DEVICES_NOTE}</p> : null}
          </>
        ) : (
          <p className="secure-score-state">
            {saysNotLicensed
              ? NOT_LICENSED_SENTENCE
              : noDevices && measured === 0
                ? NO_DEVICES_ZERO_SENTENCE
                : exposureSentence(reading, noDevices)}
          </p>
        )}

        {saysNotLicensed ? null : (
          <p className="secure-score-note exposure-vulnerabilities">{vulnerabilitySentence(reading, noDevices)}</p>
        )}

        {shown !== null || reading?.vulnerabilities.status === 'measured' ? (
          <a className="secure-score-link" href={DEFENDER_URL} target="_blank" rel="noopener noreferrer">
            Open in Microsoft Defender
          </a>
        ) : null}
      </div>
    </section>
  )
}

/**
 * The Endpoint page's panel, from the overview. Nothing until it has an answer, and nothing if the
 * read fails or answered for another workspace -- the findings list below is the page.
 */
export function AreaExposureScore({ compact = false }: { compact?: boolean } = {}) {
  const session = useSession()
  const overview = useApi(() => getOverview(), [])

  if (overview.state !== 'ready') return null
  if (!agrees(session, overview.data.workspaceId)) return null

  // From the same list the dashboard and the area rows read, so the panel cannot say "couldn't read"
  // where the rest of the page says "not licensed". An older server sends no freshness list: none.
  const freshness = overview.data.freshness
  const notLicensed =
    freshness != null && notLicensedOf(freshness).some((n) => n.service === 'defender_for_endpoint')

  return <ExposureScoreGauge reading={overview.data.defender} notLicensed={notLicensed} compact={compact} />
}

/**
 * Whether Microsoft Defender listed no onboarded devices on this read (0159), and said so
 * completely. Absent on a reading from before 0159, and false for a list that was not read or was
 * cut short (then "0 onboarded" is a minimum, not a fact): those keep today's panel.
 */
function noOnboardedDevices(reading: DefenderReading | null | undefined): boolean {
  const devices = reading?.devices
  return devices != null && devices.status === 'measured' && devices.onboarded === 0 && !devices.incomplete
}

/** Where the number would be: Microsoft's 0, given because there is nothing to measure. */
const NO_DEVICES_ZERO_SENTENCE =
  'Microsoft Defender lists no onboarded devices for your tenant, so there is nothing for its ' +
  'exposure score to measure (Microsoft shows 0 in that case).'

/** Under a score Microsoft measured above 0 on a read that listed no onboarded devices. */
const NO_DEVICES_NOTE = 'Microsoft Defender listed no onboarded devices when this was read.'

/** The vulnerability line when Defender lists no onboarded devices and so reports none. */
const NO_DEVICES_VULNERABILITIES =
  'Microsoft Defender lists no onboarded devices, so it reports no vulnerabilities.'

/** What stands where the number would be when the tenant has no Defender for Endpoint licence. */
const NOT_LICENSED_SENTENCE =
  'Not assessed: this tenant has no licence for Microsoft Defender for Endpoint, so Microsoft has no ' +
  'exposure score or vulnerability list for it. Nothing here is counted as passing.'

/** Microsoft's documented levels for the exposure score. */
export function level(score: number): 'low' | 'medium' | 'high' {
  return score < 30 ? 'low' : score < 70 ? 'medium' : 'high'
}

function ExposureArc({ score, label }: { score: number; label: string }) {
  const drawn = Math.min(100, Math.max(0, score))
  return (
    <div className="secure-score-gauge" role="img" aria-label={label}>
      <svg viewBox="0 0 120 66" aria-hidden="true" focusable="false">
        <path className="secure-score-track" d={ARC} pathLength={100} fill="none" strokeWidth={10} />
        {drawn > 0 ? (
          <path
            className="secure-score-arc"
            d={ARC}
            pathLength={100}
            fill="none"
            strokeWidth={10}
            strokeDasharray={`${drawn} 100`}
          />
        ) : null}
      </svg>
      <span className="secure-score-value" aria-hidden="true">
        {Math.round(score)}
        <span className="secure-score-unit">/100</span>
      </span>
    </div>
  )
}

const ARC = 'M 10 60 A 50 50 0 0 1 110 60'

/** What stands where the number would be. Never a zero. */
function exposureSentence(reading: DefenderReading | null | undefined, noDevices = false): string {
  if (!reading) return "Not read yet — it's read on the next assessment."

  const exposure = reading.exposure
  if (exposure.status === 'absent') {
    // With no onboarded devices that is the whole reason; "may not be set up" would send somebody
    // looking for a setting.
    return noDevices
      ? 'Microsoft Defender lists no onboarded devices for your tenant, so it reports no exposure score.'
      : "Microsoft doesn't report an exposure score for this tenant (Defender Vulnerability Management may not be set up)."
  }
  if (exposure.status === 'measured') {
    return "Microsoft's reading on the last assessment came without a usable number, so none is shown."
  }
  return `We couldn't read the exposure score on the last assessment (${day(reading.readAt)})${reason(exposure.failure)}. Nothing is concluded from that.`
}

/**
 * The line about the vulnerabilities that are NOT findings. Only the ones with a known exploit are
 * findings (0153); this is what keeps a short findings list from reading as "no vulnerabilities".
 */
function vulnerabilitySentence(reading: DefenderReading | null | undefined, noDevices = false): string {
  if (!reading) return 'Vulnerability counts are read on the next assessment.'

  const v = reading.vulnerabilities
  if (v.status === 'absent') {
    return noDevices ? NO_DEVICES_VULNERABILITIES : 'Microsoft Defender returned no vulnerability data for this tenant.'
  }
  if (v.status !== 'measured' || v.open === null || v.withKnownExploit === null) {
    return `We couldn't read Microsoft Defender's vulnerability list on the last assessment${reason(v.failure)}.`
  }

  // A list cut short says nothing about what was not read: never "none", never "no open
  // vulnerabilities", never an exact count of the others.
  if (v.truncated) {
    const found =
      v.withKnownExploit === 0
        ? 'none of those read has a known exploit'
        : `${count(v.withKnownExploit, 'has', 'have')} a known exploit and ${v.withKnownExploit === 1 ? 'is' : 'are'} listed below`
    return `Microsoft Defender returned more vulnerabilities than one assessment reads. Of the ${v.open.toLocaleString()} read, ${found}; more may have one. Microsoft Defender lists them all.`
  }

  const others = v.open - v.withKnownExploit

  // "No open vulnerabilities on your devices" over a Defender that lists no devices is a clean
  // result for machines nobody is measuring. Microsoft's zero is kept; the reason for it is said.
  if (v.open === 0) {
    return noDevices ? NO_DEVICES_VULNERABILITIES : 'Microsoft Defender reports no open vulnerabilities on your devices.'
  }

  if (v.withKnownExploit === 0) {
    return capitalise(
      `${count(v.open, 'open vulnerability', 'open vulnerabilities')}, none with a known exploit, so none is a finding here. Microsoft Defender lists them all.`,
    )
  }

  const exploitable = `${count(v.withKnownExploit, 'vulnerability', 'vulnerabilities')} with a known exploit ${v.withKnownExploit === 1 ? 'is' : 'are'} listed below.`
  return others <= 0
    ? capitalise(exploitable)
    : capitalise(
        `${exploitable} ${count(others, 'other open vulnerability has', 'other open vulnerabilities have')} no known exploit, so ${others === 1 ? "it isn't a finding" : "they aren't findings"} here; Microsoft Defender lists them all.`,
      )
}

/**
 * Why a reading was not taken, as a clause. Two vocabularies: the words a reading recorded before
 * 0159 (DefenderFailure's names), and the 0159 reason codes, worded in unreadReasons.ts so this
 * panel and the dashboard's card agree. permission_not_granted and throttled are the same word in
 * both, and read the same.
 */
function reason(failure: string | null): string {
  switch (failure) {
    // Before 0159.
    case 'no_connection':
      return ': no Microsoft tenant is connected'
    case 'not_consented':
      return ': access to Microsoft Defender for Endpoint has not been granted'
    case 'forbidden':
      return ': Microsoft refused the read (a missing permission or licence)'
    // The old words that mean exactly one 0159 reason read as that reason.
    case 'unreachable':
      return reasonClause('microsoft_unavailable')
    case 'not_available':
      return reasonClause('not_available_in_tenant')
    case 'no_data':
      return reasonClause('no_data_from_microsoft')
    // An old word that covered more than one cause, or none: no clause, as before.
    case 'service_error':
    case 'unreadable':
      return ''
    // Since 0159 (and permission_not_granted, throttled from either vocabulary).
    default:
      return reasonClause(failure)
  }
}

function count(n: number, one: string, many: string): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

function day(value: string): string {
  const when = new Date(value)
  return Number.isNaN(when.getTime()) ? value : when.toLocaleDateString(undefined, { dateStyle: 'long' })
}
